/**
 * @file Controlled Drugs mutation and callable helpers.
 */

import {
    collection as firestoreCollection,
    getDocs as firestoreGetDocs,
    limit as firestoreLimit,
    orderBy as firestoreOrderBy,
    query as firestoreQuery,
    startAfter as firestoreStartAfter,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db as configuredDb } from "../../app/firebase/firebase.js";
import {
    checkPendingApprovalsCallable as configuredCheckPendingApprovalsCallable,
    deleteActiveAuditCallable as configuredDeleteActiveAuditCallable,
    exportDrugDataCallable as configuredExportDrugDataCallable,
    finalizeAuditCallable as configuredFinalizeAuditCallable,
    getNextBulkCodeCallable as configuredGetNextBulkCodeCallable,
    handleApprovalActionCallable as configuredHandleApprovalActionCallable,
    reassignApprovalCallable as configuredReassignApprovalCallable,
    saveDrugTransactionCallable as configuredSaveDrugTransactionCallable,
    startAuditCallable as configuredStartAuditCallable,
    updateAuditProgressCallable as configuredUpdateAuditProgressCallable,
    voidTransactionCallable as configuredVoidTransactionCallable,
} from "../../app/firebase/callables.js";
import { appData as configuredAppData, state as configuredState } from "../../app/state/state.js";
import { renderCurrentBoard as configuredRenderCurrentBoard } from "../../app/view/viewController.js";
import { closeForm as configuredCloseForm } from "../../shared/forms/modalShell.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    formatDate as configuredFormatDate,
} from "../../shared/utils/dateUtils.js";
import {
    sanitizeHTML as configuredSanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import { renderDrugsView as configuredRenderDrugsView } from "./renderDrugs.js";

/**
 * Creates Controlled Drugs API helpers with Firebase callables injected by the
 * legacy compatibility API surface.
 * @param {object} deps Dependency bag.
 * @returns {object} Controlled Drugs API helpers.
 */
export function createDrugApi({
    appData,
    closeForm,
    collection,
    db,
    deleteActiveAuditCallable,
    exportDrugDataCallable,
    finalizeAuditCallable,
    formatDate,
    getDocs,
    getNextBulkCodeCallable,
    handleApprovalActionCallable,
    limit,
    openConfirmationModal,
    orderBy,
    query,
    reassignApprovalCallable,
    renderCurrentBoard,
    renderDrugsView,
    sanitizeHTML,
    saveDrugTransactionCallable,
    startAfter,
    startAuditCallable,
    state,
    updateAuditProgressCallable,
    checkPendingApprovalsCallable,
    voidTransactionCallable,
}) {
/**
 * Saves a new drug transaction (with duplicate check).
 * @param {object} transactionData - The transaction payload.
 * @returns {Promise<boolean>} Success status.
 */
async function saveDrugTransaction(transactionData) {
    if (!transactionData) {
        openConfirmationModal(
            'Validation Error',
            'Missing required transaction data.',
            () => { },
            false,
            'OK',
        );
        return false;
    }

    // --- Helper for formatting Duplicate Message ---
    // Look up drug and location details for the warning message
    const getDuplicateDetailsHtml = (duplicateTx = null) => {
        // Use duplicate transaction as source if available, else current transaction
        const sourceTx = duplicateTx || transactionData;

        // 1. Header Details (Patient & Date)
        const patientName = sourceTx.patientName || sourceTx.patient || 'Unknown Patient';

        // Use duplicate's date specifically if we have a match, otherwise fallback
        let dateVal = transactionData.transactionDate;
        if (duplicateTx) {
            dateVal = duplicateTx.transactionDate || duplicateTx.createdAt;
        }

        let tDate = 'Unknown Date';
        try {
            tDate = formatDate(dateVal);
        } catch { }

        // 2. Items Table
        const items = sourceTx.items || sourceTx.payload || [];

        // Helper to resolve location name
        const resolveLocName = (locId) => {
            if (!locId) {return '-';}
            const locObj = (appData.config?.drugs?.locations || []).find(l => l.id === locId);
            return locObj ? locObj.name : locId;
        };

        // Helper to resolve drug short name
        const resolveDrugName = (dId) => {
            if (!dId) {return 'Unknown';}
            const d = (appData.drugInventory || []).find(x => x.id === dId);
            return d ? (d.shortName || d.name) : 'Unknown';
        };

        // Normalize items for display
        let displayItems = [];
        if (items.length > 0) {
            displayItems = items.map(item => ({
                drug: resolveDrugName(item.drugId),
                qty: item.quantity,
                loc: resolveLocName(item.location || sourceTx.fromLocation || sourceTx.location)
            }));
        } else {
            // Fallback for flat structure
            displayItems.push({
                drug: resolveDrugName(sourceTx.drugId),
                qty: sourceTx.quantity,
                loc: resolveLocName(sourceTx.fromLocation || sourceTx.location)
            });
        }

        const rows = displayItems.map(item => `
            <tr>
                <td class="px-3 py-2 text-sm text-gray-900">${sanitizeHTML(item.drug)}</td>
                <td class="px-3 py-2 text-sm text-gray-900">${item.qty}</td>
                <td class="px-3 py-2 text-sm text-gray-900">${sanitizeHTML(item.loc)}</td>
            </tr>
        `).join('');

        return `
            <div class="text-left mt-4 mb-4">
                <div class="mb-3 space-y-1">
                    <div class="text-sm font-bold text-gray-700">Patient: <span class="font-normal text-gray-900">${sanitizeHTML(patientName)}</span></div>
                    <div class="text-sm font-bold text-gray-700">Date: <span class="font-normal text-gray-900">${tDate}</span></div>
                </div>

                <div class="border rounded-md overflow-hidden bg-white shadow-sm">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Drug</th>
                                <th scope="col" class="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Qty</th>
                                <th scope="col" class="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200">
                            ${rows}
                        </tbody>
                    </table>
                </div>
            </div>
            <p class="text-gray-800 dark:text-gray-200 mt-4 text-sm">Do you want to proceed with this likely duplicate?</p>
        `;
    };

    /** Helper to check if a transaction in cache matches the current attempt */
    const isDuplicateMatch = (t, attempt, uidCheck = null) => {
        if (!t || !attempt) {return false;}
        if (t.type !== attempt.type) {return false;}
        if (uidCheck && t.performedBy !== uidCheck) {return false;}

        // Unified patient check (attempt uses 'patient', cached 't' uses 'patientName')
        const tPat = (t.patientName || t.patient || '').trim().toLowerCase();
        const aPat = (attempt.patient || attempt.patientName || '').trim().toLowerCase();
        if (tPat !== aPat) {return false;}

        const tItems = t.payload || t.items || [];
        const aItems = attempt.items || attempt.payload || [];

        const tIsMulti = tItems.length > 0;
        const aIsMulti = aItems.length > 0;

        // Case 1: Both are multi-item
        if (tIsMulti && aIsMulti) {
            if (tItems.length !== aItems.length) {return false;}
            for (let i = 0; i < tItems.length; i++) {
                const ti = tItems[i];
                const ai = aItems[i];
                if (ti.drugId !== ai.drugId) {return false;}
                if (Number(ti.quantity) !== Number(ai.quantity)) {return false;}

                // Location check (item level or fallback to top level)
                const til = ti.location || t.location || t.fromLocation;
                const ail = ai.location || attempt.location || attempt.fromLocation;
                if (til !== ail) {return false;}
            }
            return true;
        }

        // Case 2: Both are legacy flat structure
        if (!tIsMulti && !aIsMulti) {
            // Must have a drugId to match flat. Prevents undefined === undefined matching.
            if (!t.drugId || !attempt.drugId) {return false;}

            const tLoc = t.location || t.fromLocation;
            const aLoc = attempt.location || attempt.fromLocation;

            return t.drugId === attempt.drugId &&
                tLoc === aLoc &&
                Number(t.quantity || 0) === Number(attempt.quantity || 0);
        }

        // Case 3: Mixed style (e.g., trying to match a 1-item auto-converted transaction)
        if (tIsMulti && !aIsMulti && tItems.length === 1) {
            const ti = tItems[0];
            const til = ti.location || t.location || t.fromLocation;
            const aLoc = attempt.location || attempt.fromLocation;
            return ti.drugId === attempt.drugId && til === aLoc && Number(ti.quantity) === Number(attempt.quantity);
        }
        if (!tIsMulti && aIsMulti && aItems.length === 1) {
            const ai = aItems[0];
            const ail = ai.location || attempt.location || attempt.fromLocation;
            const tLoc = t.location || t.fromLocation;
            return ai.drugId === t.drugId && ail === tLoc && Number(ai.quantity) === Number(t.quantity);
        }

        return false;
    };

    // --- 2. DUPLICATE CHECK (Client-Side) ---
    // We check the local cache for a recent transaction that looks identical.
    const recentTransactions = appData.drugTransactions || [];
    const now = Date.now();
    const fiveMinsMsg = 5 * 60 * 1000;

    // Safety check for user ID (though currentUser should exist)
    const currentUid = state.currentUser?.uid;

    const potentiallyDuplicate = recentTransactions.find(t => {
        // Check time (created recently)
        const tTime = t.createdAt ? (t.createdAt.seconds * 1000) : 0;
        if (now - tTime > fiveMinsMsg) {return false;}

        return isDuplicateMatch(t, transactionData, currentUid);
    });

    if (potentiallyDuplicate && !transactionData.confirmedDuplicate) {
        // Wrap in promise to pause execution until modal decision
        return new Promise((resolve) => {
            const messageHtml = getDuplicateDetailsHtml(potentiallyDuplicate);

            // Create a specialized payload that includes the override flag
            const overrides = { confirmedDuplicate: true };
            const payloadWithOverride = { ...transactionData, ...overrides };

            openConfirmationModal(
                'Duplicate Transaction Warning',
                `Warning: A similar transaction was submitted recently.<br>${messageHtml}`,
                async () => {
                    // User clicked "Continue" - proceed to save WITH OVERRIDE
                    try {
                        const result = await saveDrugTransactionCallable(payloadWithOverride);
                        resolve(result.data);
                    } catch (err) {
                        console.error('Error saving after duplicate confirmation:', err);
                        const errorMessage = (err.code === 'internal' || !err.message)
                            ? 'Something went wrong. Please try again later.'
                            : `Failed to save: ${err.message}`;
                        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
                        resolve({ success: false });
                    }
                },
                false,
                'Continue', // Button text
                () => resolve({ success: false }) // Cancel callback
            );
        });
    }

    try {
        const result = await saveDrugTransactionCallable(transactionData);
        return result.data;
    } catch (error) {
        console.error('Error saving drug transaction:', error);

        // Handle ALREADY EXISTS (Duplicate detected by Backend) 
        // This is a failsafe if client check misses it
        // Check code OR message content to be safe
        if (error.code === 'already-exists' || (error.message && error.message.toLowerCase().includes('duplicate'))) {
            return new Promise((resolve) => {
                // Attempt to find the duplicate in local cache (relaxed match mirroring backend logic:
                // same type + same patient + at least one overlapping drug ID).
                // This is intentionally looser than the client-side 5-min check because
                // the backend uses a wider window and only requires partial drug overlap.
                const currentDrugIds = new Set((transactionData.items || []).map(i => i.drugId));
                const currentPatient = (transactionData.patient || transactionData.patientName || '').trim().toLowerCase();

                const likelyDuplicate = (appData.drugTransactions || []).find(t => {
                    if (t.type !== transactionData.type) {return false;}
                    const tPat = (t.patientName || '').trim().toLowerCase();
                    if (tPat !== currentPatient) {return false;}
                    const tPayload = t.payload || [];
                    if (tPayload.length === 0 || currentDrugIds.size === 0) {return false;}
                    return tPayload.some(p => currentDrugIds.has(p.drugId));
                });

                const messageHtml = getDuplicateDetailsHtml(likelyDuplicate); // Reuse our detail generator

                openConfirmationModal(
                    'Duplicate Transaction Warning',
                    `System detected a potential duplicate.<br>${messageHtml}`,
                    async () => {
                        try {
                            // Add override flag
                            const payloadWithOverride = { ...transactionData, confirmedDuplicate: true };
                            const result = await saveDrugTransactionCallable(payloadWithOverride);
                            resolve(result.data);
                        } catch (retryErr) {
                            const msg = retryErr.message || 'Retry failed.';
                            openConfirmationModal('Error', msg, () => { }, false, 'OK');
                            resolve({ success: false });
                        }
                    },
                    false,
                    'Proceed',
                    () => resolve({ success: false }) // Cancel callback
                );
            });
        }

        // Handle other errors
        const errorMessage = (error.code === 'internal' || !error.message)
            ? 'Something went wrong. Please try again later.'
            : `Failed to save transaction: ${error.message}`;
        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        return { success: false };
    }
}

/**
 * Handles approval actions.
 * @param {string} approvalId - The ID of the approval document.
 * @param {'approve'|'reject'} action - The action to take.
 * @param {string} [comment] - Optional comment for the decision.
 * @returns {Promise<void>}
 */
async function handleApprovalAction(approvalId, action, comment = null) {
    if (!approvalId || !action) {
        openConfirmationModal(
            'Validation Error',
            'Missing required approval data.',
            () => { },
            false,
            'OK',
        );
        return false;
    }

    try {
        const result = await handleApprovalActionCallable({
            approvalId,
            action,
            comment: comment || null,
        });

        if (result.data.success) {
            // Success - Firestore listeners will update UI automatically
            closeForm();
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error handling approval action:', error);

        let errorMessage = `Failed to ${action} transaction: ${error.message}`;
        // Specific user-friendly error for system rules rejection
        if (error.code === 'permission-denied') {
            // If error message contains specific details from backend, use them, otherwise generic
            if (error.message && error.message.includes('system rules')) {
                errorMessage = error.message; // "Available by system rules"
            } else {
                errorMessage = 'Action denied by system permission rules.';
            }
        } else if (error.code === 'internal' || !error.message) {
            errorMessage = 'Something went wrong. Please try again later.';
        }

        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        return false;
    }
}

/**
 * Starts a new drug audit.
 * @returns {Promise<object|null>} - Returns result object (potentially with approvalId) or null on failure.
 */
async function startAudit() {
    try {
        const result = await startAuditCallable({});

        if (result.data.success) {
            // Check if approval was required
            if (result.data.approvalId) {
                return result.data; // Return full data for UI to handle confirmation
            }

            // Force immediate re-render to show audit console
            // Use setTimeout to allow Firestore listener to update appData first
            setTimeout(() => {
                if (typeof renderCurrentBoard === 'function') {
                    renderCurrentBoard();
                }
            }, 100);
            return result.data;
        }
    } catch (error) {
        console.error('Error starting audit:', error);
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error.code === 'failed-precondition') {
            errorMessage = 'An audit is already in progress. Please finalise or delete the existing audit first.';
        } else if (error.code === 'permission-denied') {
            // Use backend message if specific
            errorMessage = error.message || 'You do not have permission to start an audit.';
        } else if (error.message) {
            errorMessage = `Failed to start audit: ${error.message}`;
        }
        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        throw error;
    }
}

/**
 * Checks for pending approvals that block an audit.
 * @returns {Promise<object>} Result containing pendingApprovals array.
 */
async function checkPendingApprovals() {
    try {
        const result = await checkPendingApprovalsCallable();
        return result.data;
    } catch (error) {
        console.error('Error checking pending approvals:', error);
        throw error;
    }
}


/**
 * Finalizes an audit.
 * @param {string} auditId - The ID of the audit to finalize.
 * @param {object} actuals - Map of actual stock counts.
 * @param {object} discrepancyNotes - Map of notes.
 * @returns {Promise<object|null>}
 */
async function finalizeAudit(auditId, actuals, discrepancyNotes = {}, force = false) {
    if (!auditId || !actuals) {
        openConfirmationModal(
            'Validation Error',
            'Missing required audit data.',
            () => { },
            false,
            'OK',
        );
        return null; // Return null to indicate failure
    }

    try {
        const result = await finalizeAuditCallable({
            auditId,
            actuals,
            discrepancyNotes,
            force,
        });

        if (result.data) {
            return result.data; // Return result for potential approval processing or error handling
        }
    } catch (error) {
        console.error('Error finalizing audit:', error);
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error.code === 'invalid-argument') {
            errorMessage = error.message || 'Invalid audit data. Please check all counts are valid.';
        } else if (error.code === 'failed-precondition') {
            errorMessage = 'Audit is not in progress. It may have already been finalised or deleted.';
        } else if (error.code === 'permission-denied') {
            errorMessage = error.message || 'You do not have permission to finalise audits.';
        } else if (error.message) {
            errorMessage = `Failed to finalise audit: ${error.message}`;
        }

        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        throw error;
    }
}

/**
 * Deletes or Cancels an active (in-progress) audit.
 * Used for both hard Delete and soft Cancel.
 * @param {string} [reason] - The reason for cancellation.
 * @returns {Promise<object|null>}
 */
async function deleteActiveAudit(reason, force = false) {
    // Look up active audit ID if not provided as first arg (handling legacy/incorrect calls)
    const cancelReason = reason;

    // Check if first arg is actually ID (unlikely in current usage but safe to check)
    // Actually, renderDrugs.js passes 'reason' as first arg.
    // So we should assume first arg IS reason, and we find ID locally.

    // Find active audit ID locally
    const activeAudit = (appData.drugAudits || []).find(a => a.status === 'in_progress');
    if (!activeAudit) {
        openConfirmationModal(
            'Error',
            'No active audit found to cancel.',
            () => { },
            false,
            'OK',
        );
        return null; // No active audit locally
    }
    const auditId = activeAudit.id;

    if (!cancelReason) {
        openConfirmationModal(
            'Validation Error',
            'A reason is required to cancel an audit.',
            () => { },
            false,
            'OK',
        );
        return null;
    }

    try {
        const result = await deleteActiveAuditCallable({ auditId, reason: cancelReason, force });

        if (result.data) {
            return result.data;
        }
    } catch (error) {
        console.error('Error cancelling/deleting audit:', error);
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error.code === 'failed-precondition') {
            errorMessage = 'Can only delete in-progress audits.';
        } else if (error.code === 'permission-denied') {
            errorMessage = error.message || 'You do not have permission to delete/cancel audits.';
        } else if (error.message) {
            errorMessage = `Failed to cancel audit: ${error.message}`;
        }

        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        throw error;
    }
}

/**
 * Updates audit progress (auto-save).
 * @param {string} auditId 
 * @param {object} actuals 
 * @param {object} discrepancyNotes 
 * @returns {Promise<void>}
 */
async function updateAuditProgress(auditId, actuals, discrepancyNotes = {}) {
    if (!auditId) {return;}

    try {
        await updateAuditProgressCallable({
            auditId,
            actuals,
            discrepancyNotes,
        });
        // Silent success - no UI feedback needed for auto-save
    } catch (error) {
        console.error('Error updating audit progress:', error);
        // Silent fail - don't interrupt user workflow
    }
}

/**
 * Voids a drug transaction.
 * @param {string} transactionId - The ID of the transaction to void.
 * @param {string} reason - The reason for voiding.
 * @returns {Promise<object|null>}
 */
async function voidTransaction(transactionId, reason) {
    if (!transactionId || !reason) {
        openConfirmationModal(
            'Validation Error',
            'Transaction ID and reason are required.',
            () => { },
            false,
            'OK',
        );
        return null;
    }

    try {
        const result = await voidTransactionCallable({ transactionId, reason });
        return result.data;
    } catch (error) {
        console.error('Error voiding transaction:', error);
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error.code === 'failed-precondition') {
            errorMessage = error.message || 'Transaction cannot be voided.';
        } else if (error.code === 'permission-denied') {
            errorMessage = error.message || 'You can only void your own transactions. Contact a Manager to void this transaction.';
        } else if (error.message) {
            errorMessage = `Failed to void transaction: ${error.message}`;
        }

        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        throw error;
    }
}

/**
 * Exports drug data.
 * @param {object} options - Export options { type, auditId, ... }
 * @returns {Promise<object>}
 */
async function exportDrugData(options) {
    try {
        const result = await exportDrugDataCallable(options);

        // if (result.data.success) {
        //     openConfirmationModal(
        //         'Export Requested',
        //         result.data.message || 'Export functionality is being implemented. The export will be available soon.',
        //         () => { },
        //         false,
        //         'OK',
        //     );
        // }
        return result.data;
    } catch (error) {
        console.error('Error exporting drug data:', error);
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error.code === 'permission-denied') {
            errorMessage = 'You do not have permission to export drug data.';
        } else if (error.message) {
            errorMessage = `Failed to export data: ${error.message}`;
        }

        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
    }
}

/**
 * Gets the next available bulk transaction code (Preview).
 * @returns {Promise<string|null>} e.g., "CS-005"
 */
async function getNextBulkCode() {
    try {
        const result = await getNextBulkCodeCallable({});
        return result.data.code;
    } catch (error) {
        console.error('Error fetching bulk code:', error);
        return null;
    }
}


/**
 * Loads more drug transactions for pagination.
 * Used when "Load More" button is clicked in the history view.
 * @param {DocumentSnapshot} lastDoc - The last document from the previous query (for pagination).
 * @returns {Promise<void>}
 */
async function loadMoreDrugTransactions(lastDoc) {
    if (!lastDoc) {
        console.warn('loadMoreDrugTransactions: No lastDoc provided');
        return;
    }

    const paginationState = state.drugHistoryPagination;
    if (!paginationState.hasMore) {
        console.warn('loadMoreDrugTransactions: No more items to load');
        return;
    }

    try {
        const TRANSACTIONS_LIMIT = 50;
        const nextQuery = query(
            collection(db, 'drug_transactions'),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(TRANSACTIONS_LIMIT),
        );

        const snapshot = await getDocs(nextQuery);
        const newTransactions = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
        }));

        if (newTransactions.length > 0) {
            // Merge with existing transactions (avoid duplicates)
            const existingTransactions = appData.drugTransactions || [];
            const existingIds = new Set(existingTransactions.map((tx) => tx.id));
            const uniqueNewTransactions = newTransactions.filter(
                (tx) => !existingIds.has(tx.id),
            );

            appData.drugTransactions = [
                ...existingTransactions,
                ...uniqueNewTransactions,
            ];

            // Update pagination state
            state.drugHistoryPagination.hasMore =
                snapshot.docs.length === TRANSACTIONS_LIMIT;
            state.drugHistoryPagination.lastDoc =
                snapshot.docs.length > 0
                    ? snapshot.docs[snapshot.docs.length - 1]
                    : null;

            // Re-render the drugs view
            if (state.currentView === 'drugs') {
                renderDrugsView();
            }
        } else {
            // No more items
            state.drugHistoryPagination.hasMore = false;
        }
    } catch (error) {
        console.error('Error loading more drug transactions:', error);
        const errorMessage = (error.code === 'internal' || !error.message)
            ? 'Something went wrong. Please try again later.'
            : `Failed to load more transactions: ${error.message}`;
        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
    }
}
/**
 * Reassigns an approval to another user.
 * @param {string} approvalId 
 * @param {string} newApproverId 
 * @returns {Promise<object>} Result { success: true, ... }
 */
async function reassignApproval(approvalId, newApproverId) {
    if (!approvalId || !newApproverId) {return { success: false, message: "Missing required data." };}

    try {
        const result = await reassignApprovalCallable({ approvalId, newApproverId });
        return result.data;
    } catch (error) {
        console.error('Error reassigning approval:', error);
        let errorMessage = 'Something went wrong.';
        if (error.code === 'permission-denied') {
            errorMessage = error.message || 'You do not have permission to reassign this approval.';
        } else if (error.message) {
            errorMessage = error.message;
        }
        openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
        throw error;
    }
}

    return {
        saveDrugTransaction,
        handleApprovalAction,
        startAudit,
        checkPendingApprovals,
        finalizeAudit,
        deleteActiveAudit,
        updateAuditProgress,
        voidTransaction,
        exportDrugData,
        getNextBulkCode,
        loadMoreDrugTransactions,
        reassignApproval,
    };
}

const configuredDrugApi = createDrugApi({
    appData: configuredAppData,
    closeForm: configuredCloseForm,
    collection: firestoreCollection,
    db: configuredDb,
    deleteActiveAuditCallable: configuredDeleteActiveAuditCallable,
    exportDrugDataCallable: configuredExportDrugDataCallable,
    finalizeAuditCallable: configuredFinalizeAuditCallable,
    formatDate: configuredFormatDate,
    getDocs: firestoreGetDocs,
    getNextBulkCodeCallable: configuredGetNextBulkCodeCallable,
    handleApprovalActionCallable: configuredHandleApprovalActionCallable,
    limit: firestoreLimit,
    openConfirmationModal: configuredOpenConfirmationModal,
    orderBy: firestoreOrderBy,
    query: firestoreQuery,
    reassignApprovalCallable: configuredReassignApprovalCallable,
    renderCurrentBoard: (...args) => configuredRenderCurrentBoard(...args),
    renderDrugsView: (...args) => configuredRenderDrugsView(...args),
    sanitizeHTML: configuredSanitizeHTML,
    saveDrugTransactionCallable: configuredSaveDrugTransactionCallable,
    startAfter: firestoreStartAfter,
    startAuditCallable: configuredStartAuditCallable,
    state: configuredState,
    updateAuditProgressCallable: configuredUpdateAuditProgressCallable,
    checkPendingApprovalsCallable: configuredCheckPendingApprovalsCallable,
    voidTransactionCallable: configuredVoidTransactionCallable,
});

export const saveDrugTransaction = configuredDrugApi.saveDrugTransaction;
export const handleApprovalAction = configuredDrugApi.handleApprovalAction;
export const startAudit = configuredDrugApi.startAudit;
export const checkPendingApprovals = configuredDrugApi.checkPendingApprovals;
export const finalizeAudit = configuredDrugApi.finalizeAudit;
export const deleteActiveAudit = configuredDrugApi.deleteActiveAudit;
export const updateAuditProgress = configuredDrugApi.updateAuditProgress;
export const voidTransaction = configuredDrugApi.voidTransaction;
export const exportDrugData = configuredDrugApi.exportDrugData;
export const getNextBulkCode = configuredDrugApi.getNextBulkCode;
export const loadMoreDrugTransactions = configuredDrugApi.loadMoreDrugTransactions;
export const reassignApproval = configuredDrugApi.reassignApproval;
